import axios from 'axios';

let API_BASE_URL = '/api';
if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) {
    API_BASE_URL = process.env.REACT_APP_API_URL;
}

const api = axios.create({ baseURL: API_BASE_URL });

const rpcCache = new Map();
const rpcInflight = new Map();

function getTenantCacheKey() {
    if (typeof localStorage === 'undefined') return '';
    return localStorage.getItem('upos_active_tenant_id') || '';
}

export function clearApiCache() {
    rpcCache.clear();
    rpcInflight.clear();
}

const ACCOUNTING_CACHE_MARKERS = [
    'dashboard:', 'reports:', 'sales', 'invoices:', 'bank:', 'accounting:',
    'journal', 'customers:getSummary', 'customers:getCredit',
];

const PURCHASE_CACHE_MARKERS = [
    ...ACCOUNTING_CACHE_MARKERS, 'purchases:', 'suppliers:',
];

function invalidateApiCache(markers) {
    if (!markers?.length) {
        clearApiCache();
        return;
    }
    for (const key of rpcCache.keys()) {
        if (markers.some((m) => key.includes(m))) rpcCache.delete(key);
    }
    for (const key of rpcInflight.keys()) {
        if (markers.some((m) => key.includes(m))) rpcInflight.delete(key);
    }
}

/** Broadcast after any transaction that affects cash, bank, or GL balances. */
export function notifyAccountingChanged() {
    invalidateApiCache(ACCOUNTING_CACHE_MARKERS);
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('upos:accounting-changed'));
        window.dispatchEvent(new CustomEvent('upos:bank-changed'));
        window.dispatchEvent(new CustomEvent('upos:sales-changed'));
    }
}

export function notifyPurchaseChanged() {
    invalidateApiCache(PURCHASE_CACHE_MARKERS);
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('upos:purchases-changed'));
        window.dispatchEvent(new CustomEvent('upos:accounting-changed'));
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('upos:tenant-changed', clearApiCache);
}

async function cachedRpc(cacheKey, ttlMs, loader) {
    const now = Date.now();
    const hit = rpcCache.get(cacheKey);
    if (hit && hit.expires > now) return hit.value;
    if (rpcInflight.has(cacheKey)) return rpcInflight.get(cacheKey);
    const pending = loader().then((value) => {
        rpcCache.set(cacheKey, { value, expires: Date.now() + ttlMs });
        rpcInflight.delete(cacheKey);
        return value;
    }).catch((err) => {
        rpcInflight.delete(cacheKey);
        throw err;
    });
    rpcInflight.set(cacheKey, pending);
    return pending;
}

function referenceRpc(channel, args = [], ttlMs = 60_000) {
    const key = `${getTenantCacheKey()}:${channel}:${JSON.stringify(args)}`;
    return cachedRpc(key, ttlMs, () => rpc(channel, ...args));
}

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('upos_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    const activeTenantId = localStorage.getItem('upos_active_tenant_id');
    if (activeTenantId) {
        config.headers['X-Tenant-Id'] = activeTenantId;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('upos_token');
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

async function rpc(channel, ...args) {
    try {
        const response = await api.post('/rpc', { channel, args });
        const data = response.data;
        if (data && data.success === false && !data.error) {
            data.error = data.message || data.errors?.[0] || 'Request failed';
        }
        return data;
    } catch (error) {
        const payload = error.response?.data;
        return {
            success: false,
            error: payload?.error || payload?.message || error.message || 'Request failed',
        };
    }
}

const apiService = {
    rpc,

    auth: {
        login: async (username, password) => {
            const response = await api.post('/auth/login', { username, password });
            if (response.data.success && response.data.token) {
                localStorage.setItem('upos_token', response.data.token);
            }
            return response.data;
        },
        verifyOtp: async (otpSessionId, code) => {
            const response = await api.post('/auth/verify-otp', { otpSessionId, code });
            if (response.data.success && response.data.token) {
                localStorage.setItem('upos_token', response.data.token);
            }
            return response.data;
        },
        resendOtp: (otpSessionId) => api.post('/auth/resend-otp', { otpSessionId }).then(r => r.data),
        forgotPassword: (usernameOrEmail, tenantId) =>
            api.post('/auth/forgot-password', { username: usernameOrEmail, email: usernameOrEmail.includes('@') ? usernameOrEmail : undefined, tenantId }).then(r => r.data),
        resetPassword: (username, code, newPassword, tenantId) =>
            api.post('/auth/reset-password', { username, code, newPassword, tenantId }).then(r => r.data),
        get2FAStatus: () => rpc('auth:get2FAStatus'),
        setup2FA: () => rpc('auth:setup2FA'),
        enable2FA: (code) => rpc('auth:enable2FA', code),
        getCurrentUser: async () => {
            try {
                const response = await api.get('/auth/me');
                return response.data.success ? response.data.user : null;
            } catch {
                return null;
            }
        },
        logout: async () => {
            localStorage.removeItem('upos_token');
            try {
                await api.post('/auth/logout');
            } catch { /* ignore */ }
            return { success: true };
        },
        changePassword: (oldPassword, newPassword) => rpc('auth:changePassword', oldPassword, newPassword),
        createUser: (userData) => rpc('auth:createUser', userData),
        updateUser: (userId, userData) => rpc('auth:updateUser', userId, userData),
        deleteUser: (userId) => rpc('auth:deleteUser', userId),
        getUsers: () => rpc('auth:getUsers'),
    },

    products: {
        getAll: (filters) => rpc('products:getAll', filters),
        getStats: () => referenceRpc('products:getStats', [], 30_000),
        getById: (id) => rpc('products:getById', id),
        create: (product) => rpc('products:create', product),
        update: (id, product) => rpc('products:update', id, product),
        delete: (id) => rpc('products:delete', id),
        search: (query) => rpc('products:search', query),
        getByBarcode: (barcode) => rpc('products:getByBarcode', barcode),
        updateStock: (id, quantity, type) => rpc('products:updateStock', id, quantity, type),
        getLowStock: () => rpc('products:getLowStock'),
        getCategories: () => referenceRpc('products:getCategories', [], 120_000),
        createCategory: (category) => rpc('products:createCategory', category),
        addBatch: (batch) => rpc('products:addBatch', batch),
        deleteBatch: (id) => rpc('products:deleteBatch', id),
    },

    sales: {
        getAll: (filters) => rpc('sales:getAll', filters),
        getById: (id) => rpc('sales:getById', id),
        void: (id, reason) => rpc('sales:void', id, reason),
        getToday: () => rpc('sales:getToday'),
        getDailySummary: (date) => rpc('sales:getDailySummary', date),
        printReceipt: (saleId) => rpc('sales:printReceipt', saleId),
    },

    customers: {
        getAll: (filters) => rpc('customers:getAll', filters),
        getSummary: () => referenceRpc('customers:getSummary', [], 60_000),
        getById: (id) => rpc('customers:getById', id),
        create: (customer) => rpc('customers:create', customer),
        update: (id, customer) => rpc('customers:update', id, customer),
        delete: (id) => rpc('customers:delete', id),
        search: (query) => rpc('customers:search', query),
        getBalance: (id) => rpc('customers:getBalance', id),
        addPayment: (id, paymentData) => rpc('customers:addPayment', id, paymentData),
        getCreditSummary: (id) => rpc('customers:getCreditSummary', id),
        getTransactions: (id) => rpc('customers:getTransactions', id),
    },

    reports: {
        getSalesSummary: (filters) => rpc('reports:salesSummary', filters),
        getSalesDetail: (filters) => rpc('reports:salesDetail', filters),
        getInventoryReport: (filters = {}) => rpc('reports:inventory', filters),
        getProfitLoss: (startDate, endDate) => rpc('reports:profitLoss', startDate, endDate),
        getTopProducts: (startDate, endDate, limit) => rpc('reports:topProducts', startDate, endDate, limit),
        getTopCustomers: (startDate, endDate, limit) => rpc('reports:topCustomers', startDate, endDate, limit),
        getCashierReport: (startDate, endDate) => rpc('reports:cashier', startDate, endDate),
        getPurchases: (filters) => rpc('reports:purchases', filters),
        getCustomersReport: (filters) => rpc('reports:customers', filters),
        getSuppliersReport: (filters) => rpc('reports:suppliers', filters),
        getStaffPerformance: (filters) => rpc('reports:staffPerformance', filters),
        getTaxReport: (filters) => rpc('reports:tax', filters),
        getFbrReport: (filters) => rpc('reports:fbr', filters),
        getAuditReport: (filters) => rpc('reports:audit', filters),
        getLowStockReport: (filters) => rpc('reports:lowStock', filters),
        getDiscountReport: (filters) => rpc('reports:discounts', filters),
        getPaymentReport: (filters) => rpc('reports:payments', filters),
        getSalesReturnsReport: (filters) => rpc('reports:salesReturns', filters),
        getAnalytical: (filters) => rpc('reports:analytical', filters),
        exportReport: (options) => rpc('reports:export', options),
    },

    printTemplates: {
        getAll: (filters) => rpc('printTemplates:getAll', filters || {}),
        getById: (id) => rpc('printTemplates:getById', id),
        create: (data) => rpc('printTemplates:create', data),
        update: (id, data) => rpc('printTemplates:update', id, data),
        setDefault: (id) => rpc('printTemplates:setDefault', id),
        delete: (id) => rpc('printTemplates:delete', id),
        preview: (options) => rpc('printTemplates:preview', options || {}),
        seedDefaults: (options) => rpc('printTemplates:seedDefaults', options || {}),
    },

    settings: {
        get: () => referenceRpc('settings:get', [], 60_000),
        update: async (settings) => {
            const result = await rpc('settings:update', settings);
            if (result?.success !== false) {
                // Only drop settings cache — do not wipe products/customers/dashboard
                invalidateApiCache(['settings:']);
            }
            return result;
        },
        getProfile: () => rpc('settings:getProfile'),
        updateProfile: (profile) => rpc('settings:updateProfile', profile),
        getBusinessInfo: () => rpc('settings:getBusinessInfo'),
    },

    backup: {
        createLocal: () => rpc('backup:createLocal'),
        restoreLocal: (backupPath) => rpc('backup:restoreLocal', backupPath),
        getBackupList: () => rpc('backup:getList'),
    },

    fbr: {
        sendInvoice: (saleId) => rpc('fbr:sendInvoice', saleId),
        checkStatus: () => rpc('fbr:status'),
        getQueue: (filters) => rpc('fbr:getQueue', filters || {}),
        getTaxOverview: () => rpc('fbr:getTaxOverview'),
        getConfigStatus: () => rpc('fbr:getConfigStatus'),
        validatePendingTax: () => rpc('fbr:validatePendingTax'),
        processQueue: async () => {
            try {
                const response = await api.post('/rpc', {
                    channel: 'fbr:processQueue',
                    args: [],
                }, { timeout: 180000 });
                const data = response.data;
                if (data && data.success === false && !data.error) {
                    data.error = data.message || data.errors?.[0] || 'Request failed';
                }
                return data;
            } catch (error) {
                const payload = error.response?.data;
                return {
                    success: false,
                    error: payload?.error || payload?.message || error.message || 'Process Queue failed',
                };
            }
        },
        validateInvoice: (saleId) => rpc('fbr:validateInvoice', saleId),
        getLogs: (filters) => rpc('fbr:getLogs', filters || {}),
        getDailyReport: (filters) => rpc('fbr:getDailyReport', filters || {}),
        retryFailed: () => rpc('fbr:retryFailed'),
        getQrData: (saleId) => rpc('fbr:getQrData', saleId),
    },

    taxRates: {
        getAll: (filters) => rpc('taxRates:getAll', filters || {}),
        create: (data) => rpc('taxRates:create', data),
        update: (id, data) => rpc('taxRates:update', id, data),
        resolve: (options) => rpc('taxRates:resolve', options || {}),
    },

    cloud: {
        sync: () => rpc('cloud:sync'),
        getStatus: () => rpc('cloud:getStatus'),
    },

    print: {
        receipt: (saleData) => rpc('print:receipt', saleData),
        invoice: (payload) => rpc('print:invoice', payload),
        paymentReceipt: (receiptId) => rpc('print:paymentReceipt', receiptId),
        purchaseBill: (payload) => rpc('print:purchaseBill', payload),
        supplierPayment: (payload) => rpc('print:supplierPayment', payload),
        returnReceipt: (payload) => rpc('print:returnReceipt', payload),
        barcode: (product) => rpc('print:barcode', product),
        report: (reportData) => rpc('print:report', reportData),
        testPrinter: () => rpc('print:test'),
    },

    userCompany: {
        getMyCompanies: () => rpc('userCompany:getMyCompanies'),
        getAll: (filters) => rpc('userCompany:getAll', filters || {}),
        grant: (data) => rpc('userCompany:grant', data),
        revoke: (userId, tenantId) => rpc('userCompany:revoke', userId, tenantId),
    },

    accessScopes: {
        get: (userId) => rpc('accessScopes:get', userId),
        set: (userId, scopes) => rpc('accessScopes:set', userId, scopes),
    },

    audit: {
        getLogs: (filters) => rpc('auditLogs:getAll', filters || {}),
    },

    loginLogs: {
        getAll: (filters) => rpc('loginLogs:getAll', filters || {}),
    },

    preferences: {
        get: () => rpc('preferences:get'),
        saveColumns: (reportKey, columns) => rpc('preferences:saveColumns', reportKey, columns),
        saveFilters: (reportKey, filters) => rpc('preferences:saveFilters', reportKey, filters),
    },

    scheduledTasks: {
        getAll: () => rpc('scheduledTasks:getAll'),
        create: (data) => rpc('scheduledTasks:create', data),
        update: (id, data) => rpc('scheduledTasks:update', id, data),
        delete: (id) => rpc('scheduledTasks:delete', id),
        runNow: (id) => rpc('scheduledTasks:runNow', id),
    },

    notifications: {
        testEmail: (to) => rpc('notifications:testEmail', to),
        testSms: (phone) => rpc('notifications:testSms', phone),
        emailStatus: () => rpc('notifications:emailStatus'),
    },

    tenant: {
        getAll: () => rpc('tenant:getAll'),
        getById: (id) => rpc('tenant:getById', id),
        create: (dataSpec) => rpc('tenant:create', dataSpec),
        update: (id, dataSpec) => rpc('tenant:update', id, dataSpec),
        activate: (id) => rpc('tenant:activate', id),
        deactivate: (id) => rpc('tenant:deactivate', id),
        validateLicense: (key) => rpc('tenant:validateLicense', key),
    },

    utils: {
        getAppVersion: () => rpc('utils:appVersion'),
    },

    permissions: {
        getCatalog: () => rpc('permissions:getCatalog'),
        getRoles: () => rpc('permissions:getRoles'),
        getAssignableRoles: () => rpc('permissions:getAssignableRoles'),
        previewEffective: (role, customPermissions) => rpc('permissions:previewEffective', role, customPermissions),
    },

    dashboard: {
        getOverview: (filters = {}) => {
            const key = `${getTenantCacheKey()}:dashboard:getOverview:${JSON.stringify(filters)}`;
            return cachedRpc(key, 5_000, () => rpc('dashboard:getOverview', filters));
        },
    },

    bank: {
        getAccounts: () => rpc('bank:getAccounts'),
        reconcile: () => rpc('bank:reconcile'),
        getAccountStatement: (accountId, filters) => rpc('bank:getAccountStatement', accountId, filters || {}),
        createAccount: (data) => rpc('bank:createAccount', data),
        getAccountById: (id) => rpc('bank:getAccountById', id),
        updateAccount: (id, data) => rpc('bank:updateAccount', id, data),
        deactivateAccount: (id) => rpc('bank:deactivateAccount', id),
        getPayments: (filters) => rpc('bank:getPayments', filters || {}),
        createPayment: (data) => rpc('bank:createPayment', data),
        getReceipts: (filters) => rpc('bank:getReceipts', filters || {}),
        createReceipt: (data) => rpc('bank:createReceipt', data),
        getTransfers: (filters) => rpc('bank:getTransfers', filters || {}),
        createTransfer: (data) => rpc('bank:createTransfer', data),
    },

    invoices: {
        getAll: (filters) => rpc('invoices:getAll', filters || {}),
        getById: (id) => rpc('invoices:getById', id),
        create: (data) => rpc('invoices:create', data),
        approve: (id, options) => rpc('invoices:approve', id, options || {}),
        void: (id, reason) => rpc('invoices:void', id, reason),
    },

    receipts: {
        getAll: (filters) => rpc('receipts:getAll', filters || {}),
        getById: (id) => rpc('receipts:getById', id),
        create: (data) => rpc('receipts:create', data),
        update: (id, data) => rpc('receipts:update', id, data),
        delete: (id) => rpc('receipts:delete', id),
        getOpenInvoices: (customerId) => rpc('receipts:getOpenInvoices', customerId),
    },

    returns: {
        getAll: (filters) => rpc('returns:getAll', filters || {}),
        listSourceInvoices: (filters) => rpc('returns:listSourceInvoices', filters || {}),
        getSourceInvoice: (saleId) => rpc('returns:getSourceInvoice', saleId),
        create: (data) => rpc('returns:create', data),
        approve: (id) => rpc('returns:approve', id),
    },

    shifts: {
        getCurrent: () => rpc('shifts:getCurrent'),
        open: (data) => rpc('shifts:open', data || {}),
        close: (data) => rpc('shifts:close', data || {}),
        getAll: (filters) => rpc('shifts:getAll', filters || {}),
    },

    held: {
        getAll: () => rpc('held:getAll'),
        save: (data) => rpc('held:save', data),
        recall: (id) => rpc('held:recall', id),
        delete: (id) => rpc('held:delete', id),
    },

    quotations: {
        getAll: (filters) => rpc('quotations:getAll', filters || {}),
        getById: (id) => rpc('quotations:getById', id),
        create: (data) => rpc('quotations:create', data),
        update: (id, data) => rpc('quotations:update', id, data),
        convertToInvoice: (id, options) => rpc('quotations:convertToInvoice', id, options || {}),
        delete: (id) => rpc('quotations:delete', id),
    },

    expenseIncome: {
        getAll: (filters) => rpc('expenseIncome:getAll', filters || {}),
        create: (data) => rpc('expenseIncome:create', data),
        delete: (id) => rpc('expenseIncome:delete', id),
    },

    purchaseReturns: {
        getAll: (filters) => rpc('purchaseReturns:getAll', filters || {}),
        getById: (id) => rpc('purchaseReturns:getById', id),
        getSupplierSettlementInfo: (supplierId) => rpc('purchaseReturns:getSupplierSettlementInfo', supplierId),
        create: (data) => rpc('purchaseReturns:create', data),
        update: (id, data) => rpc('purchaseReturns:update', id, data),
        delete: (id, reason) => rpc('purchaseReturns:delete', id, reason),
    },

    import: {
        products: (csvText) => rpc('import:products', csvText),
        customers: (csvText) => rpc('import:customers', csvText),
    },

    locations: {
        getAll: (filters) => {
            const f = filters || {};
            if (f.isActive === true && !f.search && f.page == null) {
                return referenceRpc('locations:getAll', [f], 120_000);
            }
            return rpc('locations:getAll', f);
        },
        create: (data) => rpc('locations:create', data),
        update: (id, data) => rpc('locations:update', id, data),
        getStock: (locationId) => rpc('locations:getStock', locationId),
    },

    adjustments: {
        getAll: (filters) => rpc('adjustments:getAll', filters || {}),
        getById: (id) => rpc('adjustments:getById', id),
        create: (data) => rpc('adjustments:create', data),
        approve: (id) => rpc('adjustments:approve', id),
    },

    transfers: {
        getAll: (filters) => rpc('transfers:getAll', filters || {}),
        getById: (id) => rpc('transfers:getById', id),
        create: (data) => rpc('transfers:create', data),
        complete: (id) => rpc('transfers:complete', id),
    },

    batches: {
        getAll: (filters) => rpc('batches:getAll', filters || {}),
        create: (data) => rpc('batches:create', data),
    },

    units: {
        getAll: () => rpc('units:getAll'),
        create: (data) => rpc('units:create', data),
        getProductUnits: (productId) => rpc('units:getProductUnits', productId),
        setProductUnits: (productId, rows) => rpc('units:setProductUnits', productId, rows),
    },

    kits: {
        getAll: () => rpc('kits:getAll'),
        getByProduct: (productId) => rpc('kits:getByProduct', productId),
        save: (productId, data) => rpc('kits:save', productId, data),
    },

    inventory: {
        getValuation: (filters) => rpc('inventory:getValuation', filters || {}),
        applyLandedCost: (data) => rpc('inventory:applyLandedCost', data),
        getLowStockAlerts: (filters) => rpc('inventory:getLowStockAlerts', filters || {}),
        updateAlertRules: (productId, rules) => rpc('inventory:updateAlertRules', productId, rules),
    },

    chart: {
        getAll: () => rpc('chart:getAll'),
        create: (data) => rpc('chart:create', data),
        update: (id, data) => rpc('chart:update', id, data),
        setOpeningBalance: (id, amount) => rpc('chart:setOpeningBalance', id, amount),
    },

    journals: {
        getAll: (filters) => rpc('journals:getAll', filters || {}),
        getMeta: () => rpc('journals:getMeta'),
        getById: (id) => rpc('journals:getById', id),
        create: (data) => rpc('journals:create', data),
        updateDraft: (id, data) => rpc('journals:updateDraft', id, data),
        postDraft: (id) => rpc('journals:postDraft', id),
        deleteDraft: (id) => rpc('journals:deleteDraft', id),
        getTemplates: () => rpc('journals:getTemplates'),
        saveTemplate: (data) => rpc('journals:saveTemplate', data),
        deleteTemplate: (id) => rpc('journals:deleteTemplate', id),
        void: (id, reason) => rpc('journals:void', id, reason),
    },

    fiscal: {
        getAll: () => rpc('fiscal:getAll'),
        create: (data) => rpc('fiscal:create', data),
        setCurrent: (id) => rpc('fiscal:setCurrent', id),
        closeYear: (id) => rpc('fiscal:closeYear', id),
        reopenYear: (id) => rpc('fiscal:reopenYear', id),
        clearLock: () => rpc('fiscal:clearLock'),
        setLockDate: (lockDate) => rpc('fiscal:setLockDate', lockDate),
    },

    accounting: {
        getTrialBalance: (filters) => rpc('accounting:getTrialBalance', filters || {}),
        getProfitAndLoss: (filters) => rpc('accounting:getProfitAndLoss', filters || {}),
        getBalanceSheet: (filters) => rpc('accounting:getBalanceSheet', filters || {}),
        getCashBook: (filters) => rpc('accounting:getCashBook', filters || {}),
        getBankBook: (filters) => rpc('accounting:getBankBook', filters || {}),
        getCashFlow: (filters) => rpc('accounting:getCashFlow', filters || {}),
        getGeneralLedger: (filters) => rpc('accounting:getGeneralLedger', filters || {}),
        getDayBook: (filters) => rpc('accounting:getDayBook', filters || {}),
    },

    suppliers: {
        getAll: (filters) => rpc('suppliers:getAll', filters || {}),
        getById: (id) => rpc('suppliers:getById', id),
        getTransactions: (id) => rpc('suppliers:getTransactions', id),
        reconcileBalances: () => rpc('suppliers:reconcileBalances'),
        create: (data) => rpc('suppliers:create', data),
        update: (id, data) => rpc('suppliers:update', id, data),
        deactivate: (id) => rpc('suppliers:deactivate', id),
    },

    purchases: {
        getAll: (filters) => rpc('purchases:getAll', filters || {}),
        getById: (id) => rpc('purchases:getById', id),
        create: (data) => rpc('purchases:create', data),
        approve: (id) => rpc('purchases:approve', id),
        void: (id, reason) => rpc('purchases:void', id, reason),
        getReceiveLines: (poId) => rpc('purchases:getReceiveLines', poId),
        convertToGrn: (poId, receivedItems) => rpc('purchases:convertToGrn', poId, receivedItems),
        convertToBill: (grnId) => rpc('purchases:convertToBill', grnId),
        getApAging: (filters) => rpc('purchases:getApAging', filters || {}),
    },

    purchasePayments: {
        getAll: (filters) => rpc('purchasePayments:getAll', filters || {}),
        getById: (id) => rpc('purchasePayments:getById', id),
        create: (data) => rpc('purchasePayments:create', data),
        getOpenBills: (supplierId) => rpc('purchasePayments:getOpenBills', supplierId),
    },
};

export default apiService;
